import axios from 'axios';

// Ensure this matches the Django server address
const API_BASE_URL = 'https://soporte.ticsmartsas.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Permite enviar y recibir cookies en peticiones cross-origin
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si el error es 401 y no es un reintento ni la petición de refresh en sí misma
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/token/refresh/') {
      originalRequest._retry = true;

      try {
        // No enviamos body, el navegador envía la cookie HttpOnly automáticamente
        const res = await axios.post(`${API_BASE_URL}/token/refresh/`, {}, { withCredentials: true });
        const newAccessToken = res.data.access;

        localStorage.setItem('token', newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest); // Reintenta la petición original con el nuevo token
      } catch (refreshError) {
        console.error("Refresh token expired or invalid");
      }

      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('had_session');
    }
    if (!error.response) {
      console.error("Network Error: Please check if the Django server is running on port 8000");
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (email, password) => api.post('/token/', { email, password }),
  logout: () => api.post('/logout/'),
  register: (userData) => api.post('/users/', userData),
  requestPasswordReset: (email) => api.post('/password_reset/', { email }),
  confirmPasswordReset: (uid, token, password) => api.post('/password_reset/confirm/', { uid, token, password }),
};

export const ticketService = {
  getTickets: (params) => api.get('/tickets/', { params }),
  getTicket: (id) => api.get(`/tickets/${id}/`),
  updateTicket: (id, data) => api.patch(`/tickets/${id}/`, data),
  submitExternalTicket: (data) => api.post('/tickets/', data),
  getAgents: () => api.get('/users/'),
  followUpExternalTicket: (ticketId, email, filters = {}) => 
    api.post('/tickets/follow_up/', { ticket_id: ticketId, email, ...filters }),
  bulkReassign: (ticketIds, agentId) => 
    api.post('/tickets/bulk_reassign/', { ticket_ids: ticketIds, agent_id: agentId }),
  bulkMigrateStatus: (fromStatus, toStatus) => 
    api.post('/tickets/bulk_migrate_status/', { from_status: fromStatus, to_status: toStatus }),
  uploadAttachment: (formData) => {
    return api.post('/attachments/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addInternalNote: (ticketId, note) => 
    api.post('/notes/', { ticket: ticketId, note }),
  deleteInternalNote: (id) =>
    api.delete(`/notes/${id}/`),
  addPublicMessage: (ticketId, message, email = null) =>
    api.post('/messages/', { ticket: ticketId, message, sender_email: email }),
  sendEmail: (id, data) => 
    api.post(`/tickets/${id}/send_email/`, data)
};

export const userService = {
  getProfile: () => api.get('/users/me/'),
  updateProfile: (data) => api.patch('/users/me/', data),
  getUserLogs: (page, pageSize) => 
    api.get('/users/logs/', { params: { page, page_size: pageSize } }),
  updateAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.patch('/users/me/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  changePassword: (old_password, new_password, confirm_new_password) =>
    api.post('/users/change-password/', { old_password, new_password, confirm_new_password }),
};

export const configService = {
  getStatuses: (projectId) => api.get('/statuses/', { params: { project: projectId } }),
  createStatus: (data) => api.post('/statuses/', data),
  updateStatus: (id, data) => api.patch(`/statuses/${id}/`, data),
  deleteStatus: (id) => api.delete(`/statuses/${id}/`),
  getProjects: () => api.get('/projects/'),
  createProject: (data) => api.post('/projects/', data),
  updateProject: (id, data) => api.patch(`/projects/${id}/`, data),
  deleteProject: (id) => api.delete(`/projects/${id}/`),
  getFunctionalities: (projectId) => api.get('/functionalities/', { params: { project: projectId } }),
  createFunctionality: (data) => api.post('/functionalities/', data),
  deleteFunctionality: (id) => api.delete(`/functionalities/${id}/`),
  getSLAs: () => api.get('/sla-configs/'),
  updateSLA: (id, data) => api.patch(`/sla-configs/${id}/`, data),
  createSLA: (data) => api.post('/sla-configs/', data),
  getAssignmentConfig: () => api.get('/global-config/'),
  updateAssignmentConfig: (data) => api.post('/global-config/', data),
  getRoles: () => api.get('/role-permissions/'),
  createRole: (data) => api.post('/role-permissions/', data),
  updateRole: (id, data) => api.patch(`/role-permissions/${id}/`, data),
  deleteRole: (id) => api.delete(`/role-permissions/${id}/`),
  migrateAndDeleteRole: (id, targetRoleKey) => 
    api.post(`/role-permissions/${id}/migrate_and_delete/`, { target_role_key: targetRoleKey }),
};

export default api;